<template>
  <v-menu
    v-model="menu"
    offset-y
    max-height="304"
    :open-on-focus="true"
    :open-on-click="false"
    :close-on-click="true"
    :close-on-content-click="false"
  >
    <template #activator="{ on }">
      <w-autocomplete
        v-bind="filterProp"
        ref="autocomplete"
        :loading="isLoading"
        :value="internalValue"
        :items="flatItems"
        item-text="data.convertLabel"
        :item-value="itemValue"
        :menu-props="computedMenuProps"
        :cache-items="false"
        :value-comparator="(a, b) => a == b"
        multiple
        @keydown.tab="handleMenu(false)"
        @focus="handleMenu(true)"
        @update:search-input="updateSearch"
        @click:clear="handleClear"
        @click.native="handleNativeClick"
        v-on="on"
      >
        <template #selection="{ item }">
          <v-chip
            :color="getStateAttribute(item, 'color')"
            :style="{ 'border-color': getStateAttribute(item, 'color') }"
            close
            small
            outlined
            close-icon="$wClose"
            @click:close="handleSelectedInput(undefined, item)"
          >
            <span class="chip-label">
              {{ item.label }}
            </span>
          </v-chip>
        </template>
        <template #append>
          <MultipleSelectionFilterMenu
            v-if="filterHasFilterOptions"
            :filter-options="filterOptions"
            :filter-option-text="filterOptionText"
            :option-select-disabled="optionSelectDisabled"
            @click:filterOption="handleFilterOption"
          />
        </template>
      </w-autocomplete>
    </template>
    <w-treeview
      v-if="!hasNoItems"
      ref="treeview"
      :value="value"
      :items="lazyItems"
      :item-text="itemText"
      :item-children="itemChildren"
      item-key="id"
      hoverable
      :search="computedSearch"
      return-object
      @update:search="menu = true"
    >
      <template #prepend="{ item }">
        <w-checkbox
          v-if="allSelectable ? true : isCheckboxVisible(item)"
          :input-value="getStateAttribute(item, 'value')"
          :indeterminate="getStateAttribute(item, 'indeterminate')"
          off-icon="$wCheckboxMinus"
          @change="handleNextState($event, item)"
        />
      </template>
    </w-treeview>
  </v-menu>
</template>

<script>
import WorkAutocompleteLazy from '@/components/base_components/WorkAutocompleteLazy';
import multipleSelectionMixin from '@/components/global_components/filters/multiple_selection_filter/shared/multiple-selection-mixin';
import multipleTreeSearchMixin from '@/components/global_components/filters/multiple_selection_filter/shared/multiple-tree-search-mixin';
import multipleTreeValueSelectionMixin from '@/components/global_components/filters/multiple_selection_filter/shared/multiple-tree-value-selection-mixin';
import multipleTreeMenuMixin from '@/components/global_components/filters/multiple_selection_filter/shared/multiple-tree-menu-mixin';
import lazyLoading from '@/mixins/lazy-loading-mixin';
import { mergeData } from '@/utils/merge-data-util';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import pendingAble from '@/mixins/component/pending-able-mixin';
import { formatBool } from '@/utils/shared';

const BOOLEAN_PREFIX = {
  true: {
    prefix: 'y',
    value: true,
    color: 'var(--v-accent-base)',
    indeterminate: false,
  },
  false: {
    prefix: 'n',
    value: false,
    color: 'var(--v-error-base)',
    indeterminate: false,
  },
  undefined: { prefix: '', value: undefined, color: '', indeterminate: true },
};

const getBoolean = (value) => BOOLEAN_PREFIX[value];
const _byPrefix = (prefix) => (item) => item.prefix == prefix;
const _byId = (id) => (item) => item.id == id;
const _isBoolean = (value) => typeof value == 'boolean';

const GET_DATA = (response) => [
  {
    id: '-1:Leer',
    data: {
      label: 'Leer',
      convertLabel: 'Leer',
    },
  },
  ...response.data?.data?.map((item) => {
    item.data.convertLabel = item.data.label;
    item.data.discreteValues.map((value) => {
      value.data.convertLabel = _isBoolean(value.data.label)
        ? formatBool(value.data.label)
        : value.data.label;
      return value;
    });
    return item;
  }),
];

export default {
  name: 'WorkMultipleTreeKeyValueFilter',
  components: {
    MultipleSelectionFilterMenu: () =>
      import(
        '@/components/global_components/filters/multiple_selection_filter/shared/MultipleSelectionFilterMenu'
      ),
  },
  props: {
    itemChildren: { type: String, default: 'data.children' },
    checkboxVisible: { type: Function },
    allSelectable: { type: Boolean, default: false },
    withValue: { type: Boolean, default: true },
    withFalseValue: { type: Boolean, default: true },
    getData: { type: Function, default: GET_DATA },
    withSimpleId: { type: Boolean, default: false },
  },
  data() {
    return {
      booleanPrefix: Object.values(BOOLEAN_PREFIX),
    };
  },
  computed: {
    internalValue() {
      return this.value?.map((item) => {
        let ids = item?.split(':') ?? [];
        return this.convertId(ids);
      });
    },
    internalSelectedItems() {
      return this.value?.map((item) => {
        let values = item.split(':');
        let selectedState = BOOLEAN_PREFIX.true;
        if (this.withFalseValue)
          selectedState = this.booleanPrefix.find(_byPrefix(values[0]));
        return {
          id: this.convertId(values),
          state: selectedState,
        };
      });
    },
  },
  created() {
    this.loadInitialItems();
  },
  mixins: [
    pendingAble,
    multipleTreeMenuMixin,
    multipleTreeSearchMixin,
    multipleSelectionMixin(WorkAutocompleteLazy.props),
    multipleTreeValueSelectionMixin,
    lazyLoading(
      (self) => self.path,
      (self) =>
        mergeData(
          {},
          {
            ...self.params,
            ...self.mandatoryFilters,
            search: self.displayedString,
            ...self.sortedLabel,
          }
        ),
      10,
      (self) => self.getData
    ),
  ],
  methods: {
    isCheckboxVisible(item) {
      if (this.checkboxVisibleFunction)
        return this.checkboxVisibleFunction(item);
      return !get(item, this.itemChildren);
    },
    handleSelectedInput(state, item) {
      let newItem = get(item, this.itemValue);
      let filteredItems = [];

      if (this.withFalseValue) {
        let prefix = getBoolean(state).prefix;
        newItem = `${prefix}:${newItem}`;
      }

      switch (state) {
        case true:
          this.$emit('input', [...(this.value ?? []), newItem]);
          return;
        case false:
          filteredItems =
            this.internalSelectedItems?.filter(
              (selected) => selected.id != item.id
            ) ?? [];
          this.$emit('input', [
            ...this.mappedToFilterValue(filteredItems),
            newItem,
          ]);
          return;
        default:
          filteredItems =
            this.internalSelectedItems?.filter(
              (selected) => selected.id != item.id
            ) ?? [];
          this.$emit('input', [...this.mappedToFilterValue(filteredItems)]);
      }
    },
    handleNextState(event, item) {
      if (!this.withFalseValue) {
        this.handleSelectedInput(event === true ? true : undefined, item);
        return;
      }
      if (!event) this.handleSelectedInput(event, item);
      else {
        let valueOfCurrentItem = this.getStateAttribute(item, 'value');
        if (valueOfCurrentItem == undefined)
          this.handleSelectedInput(true, item);
        else this.handleSelectedInput(undefined, item);
      }
    },
    handleClear() {
      this.$emit('input', []);
    },
    /**
     * HELPERS
     */
    loadInitialItems() {
      if (isEmpty(this.lazyItems)) {
        this.reload();
      }
    },
    mappedToFilterValue(items) {
      return items.map((item) =>
        !this.withFalseValue ? item.id : `${item.state.prefix}:${item.id}`
      );
    },
    convertId(ids) {
      if (this.withSimpleId)
        return ids[0];

      if (!this.withFalseValue) 
        return `${ids[0]}:${ids[1]}`;

      return this.withValue ? `${ids[1]}:${ids[2]}` : `${ids[1]}`;
    },
    /**
     * GETTERS
     */
    getSelectedItem(item) {
      return this.internalSelectedItems?.find(_byId(item.id));
    },
    getStateAttribute(item, value) {
      let selectedItem = this.getSelectedItem(item);
      let state = get(selectedItem, 'state') ?? BOOLEAN_PREFIX.undefined;
      return get(state, value);
    },
  },
  watch: {
    internalValue() {
      this.$emit('update:selected', this.internalValue);
    },
    flatItems() {
      let clonedVal = cloneDeep(this.value ?? []);
      let newItems = clonedVal?.filter((val) =>
        this.flatItems?.some((item) => {
          let ids = val?.split(':') ?? [];
          return this.convertId(ids) == item.id;
        })
      );

      this.$emit('input', newItems);
    },
  },
};
</script>
<style lang="scss" scoped>
.w-select--input .v-select__selections,
.w-autocomplete--input .v-select__selections {
  .v-chip {
    margin: #{$spacer};
    border: solid;
    border-width: 0.063rem;
    background-color: $chips-bg-color !important;

    .chip-label {
      color: $chips-text-color;
    }
  }
}

::v-deep .v-treeview {
  min-height: 1rem;

  &>.v-treeview-node--leaf {
    margin-left: 0;
  }
}
</style>
